<script setup lang="ts">
import { Check, CircleClose, Warning } from '@element-plus/icons-vue'
import { computed, onMounted, onUnmounted, ref } from 'vue'

interface Props {
  title: string
  message: string
  type: 'success' | 'error' | 'warning'
}
const props = defineProps<Props>()

// Responsive icon size
const isMobile = ref(false)

function updateScreenSize() {
  isMobile.value = window.innerWidth < 640
}

onMounted(() => {
  updateScreenSize()
  window.addEventListener('resize', updateScreenSize)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateScreenSize)
})

const iconSize = computed(() => (isMobile.value ? 20 : 24))

const iconComponent = computed(() => {
  const icons = {
    success: Check,
    error: CircleClose,
    warning: Warning,
  }
  return icons[props.type]
})

const iconColor = computed(() => {
  const colors = {
    success: '#67C23A',
    error: '#F56C6C',
    warning: '#E6A23C',
  }
  return colors[props.type]
})

const iconBg = computed(() => {
  const bgColors = {
    success: 'bg-success-10',
    error: 'bg-danger-10',
    warning: 'bg-warning-10',
  }

  return bgColors[props.type]
})
</script>

<template>
  <div
    class="toast relative w-[calc(100vw-32px)] sm:w-[360px] max-w-[360px] border min-h-[80px] sm:min-h-[90px] bg-white text-black rounded-2xl sm:rounded-2xl flex items-center gap-3 sm:gap-4 px-2.5 sm:px-3 py-2.5 sm:py-3 font-inter"
  >
    <div
      class="flex items-center justify-center rounded-full p-1.5 sm:p-2 flex-shrink-0"
      :class="iconBg"
    >
      <el-icon :size="iconSize" :color="iconColor">
        <component :is="iconComponent" />
      </el-icon>
    </div>
    <div class="pr-5 sm:pr-6 flex-1 min-w-0">
      <h4
        class="font-semibold text-sm sm:text-base leading-150 mb-1 sm:mb-1.5 break-words"
      >
        {{ title }}
      </h4>
      <p class="text-xs sm:text-sm leading-125 text-text-secondary break-words">
        {{ message }}
      </p>
    </div>

    <svg
      class="toast-icon-bg"
      xmlns="http://www.w3.org/2000/svg"
      width="138"
      height="90"
      viewBox="0 0 138 90"
      fill="none"
    >
      <circle
        cx="32"
        cy="41"
        r="106"
        fill="url(#paint0_radial_1091_5469)"
        fill-opacity="0.12"
      />
      <defs>
        <radialGradient
          id="paint0_radial_1091_5469"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(32 41) rotate(90) scale(106)"
        >
          <stop stop-color="#00ED51" />
          <stop offset="1" stop-color="#00ED7B" stop-opacity="0" />
        </radialGradient>
      </defs>
    </svg>
  </div>
</template>

<style scoped>
.toast {
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    "Helvetica Neue",
    Arial,
    sans-serif;
}

.toast h4,
.toast p {
  font-family: inherit;
}

.toast-icon-bg {
  position: absolute;
  left: -74px;
  top: -65px;
  z-index: -1;
  width: 212px;
  height: 212px;
  border-radius: 50%;
}
/* Override default plugin container padding and background */
:global(.Vue-Toastification__toast) {
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
  border-radius: 16px !important;
}
:global(.Vue-Toastification__toast--success),
:global(.Vue-Toastification__toast--error),
:global(.Vue-Toastification__toast--warning),
:global(.Vue-Toastification__toast--info) {
  background: transparent !important;
  box-shadow: none !important;
}
:global(.Vue-Toastification__toast-body) {
  padding: 0 !important;
  margin: 0 !important;
  font-family: inherit !important;
}
:global(.Vue-Toastification__close-button) {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399 !important;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  padding: 0;
  transition: all 0.2s ease;
  opacity: 1 !important;
}

@media (min-width: 640px) {
  :global(.Vue-Toastification__close-button) {
    top: 12px;
    right: 12px;
    width: 24px;
    height: 24px;
    font-size: 20px;
  }
}

/* Responsive container positioning */
@media (max-width: 639px) {
  :global(.Vue-Toastification__container) {
    padding: 16px !important;
  }
}

:global(.Vue-Toastification__icon) {
  display: none !important;
}
</style>
